import { useEffect, useState } from 'react';

export default function FilterPanel({ filters, onChange, onClear, showSearchButton = false, onSearch, cuisineOptions = [] }) {
  const handleChange = (name) => (eOrValue) => {
    // support both event objects (from native inputs) and direct values (from programmatic calls)
    let value;
    if (eOrValue && typeof eOrValue === 'object' && 'target' in eOrValue) {
      const ev = eOrValue;
      value = ev.target.type === 'checkbox' ? ev.target.checked : ev.target.value;
    } else {
      value = eOrValue;
    }
    onChange && onChange(name, value);
  };

  // local state for slider so we can render thumb position smoothly
  const [localMax, setLocalMax] = useState(filters.maxCalories || 0);

  useEffect(() => {
    setLocalMax(filters.maxCalories || 0);
  }, [filters.maxCalories]);

  const handleSliderChange = (e) => {
    const v = e.target.value;
    setLocalMax(v);
    handleChange('maxCalories')(v);
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200 max-w-7xl m-auto ">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Cuisine</label>
          {Array.isArray(cuisineOptions) && cuisineOptions.length > 0 ? (
            <select
              value={filters.cuisine || ''}
              onChange={handleChange('cuisine')}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
            >
              <option value="">Any</option>
              {cuisineOptions.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              placeholder="e.g. Italian"
              value={filters.cuisine || ''}
              onChange={handleChange('cuisine')}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
            />
          )}
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Difficulty</label>
          <div className="grid grid-cols-3 gap-2 h-10">
            <button
              type="button"
              onClick={() => {
                const current = filters.difficulty || [];
                const newValue = Array.isArray(current) 
                  ? (current.includes('easy') ? current.filter(d => d !== 'easy') : [...current, 'easy'])
                  : (current === 'easy' ? [] : ['easy']);
                handleChange('difficulty')(newValue);
              }}
              className={`px-2 py-2 rounded-lg text-sm font-semibold transition-all ${
                (Array.isArray(filters.difficulty) && filters.difficulty.includes('easy')) || filters.difficulty === 'easy'
                  ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              😊 Easy
            </button>
            <button
              type="button"
              onClick={() => {
                const current = filters.difficulty || [];
                const newValue = Array.isArray(current) 
                  ? (current.includes('medium') ? current.filter(d => d !== 'medium') : [...current, 'medium'])
                  : (current === 'medium' ? [] : ['medium']);
                handleChange('difficulty')(newValue);
              }}
              className={`px-2 py-2 rounded-lg text-sm font-semibold transition-all ${
                (Array.isArray(filters.difficulty) && filters.difficulty.includes('medium')) || filters.difficulty === 'medium'
                  ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              👨‍🍳 Medium
            </button>
            <button
              type="button"
              onClick={() => {
                const current = filters.difficulty || [];
                const newValue = Array.isArray(current) 
                  ? (current.includes('hard') ? current.filter(d => d !== 'hard') : [...current, 'hard'])
                  : (current === 'hard' ? [] : ['hard']);
                handleChange('difficulty')(newValue);
              }}
              className={`px-2 py-2 rounded-lg text-sm font-semibold transition-all ${
                (Array.isArray(filters.difficulty) && filters.difficulty.includes('hard')) || filters.difficulty === 'hard'
                  ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              🔥 Hard
            </button>
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Portion Size</label>
          <div className="grid grid-cols-4 gap-2 h-10">
            <button
              type="button"
              onClick={() => handleChange('portionSize')(filters.portionSize === 1 || filters.portionSize === '1' ? '' : 1)}
              className={`px-3 py-2 rounded-lg text-sm font-semibold transition-all ${filters.portionSize === 1 || filters.portionSize === '1' ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
               1
            </button>
            <button
              type="button"
              onClick={() => handleChange('portionSize')(filters.portionSize === 2 || filters.portionSize === '2' ? '' : 2)}
              className={`px-3 py-2 rounded-lg text-sm font-semibold transition-all ${filters.portionSize === 2 || filters.portionSize === '2' ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              2
            </button>
            <button
              type="button"
              onClick={() => handleChange('portionSize')(filters.portionSize === 4 || filters.portionSize === '4' ? '' : 4)}
              className={`px-3 py-2 rounded-lg text-sm font-semibold transition-all ${filters.portionSize === 4 || filters.portionSize === '4' ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              4
            </button>
            <button
              type="button"
              onClick={() => handleChange('portionSize')(filters.portionSize === 6 || filters.portionSize === '6' ? '' : 6)}
              className={`px-3 py-2 rounded-lg text-sm font-semibold transition-all ${filters.portionSize === 6 || filters.portionSize === '6' ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              6+
            </button>
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Max Prep Time</label>
          <div className="grid grid-cols-3 gap-2 h-10">
            <button
              type="button"
              onClick={() => handleChange('prepTime')(filters.prepTime === 15 || filters.prepTime === '15' ? '' : 15)}
              className={`px-2 py-2 rounded-lg text-sm font-semibold transition-all ${filters.prepTime === 15 || filters.prepTime === '15' ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              ⚡ 15min
            </button>
            <button
              type="button"
              onClick={() => handleChange('prepTime')(filters.prepTime === 30 || filters.prepTime === '30' ? '' : 30)}
              className={`px-2 py-2 rounded-lg text-sm font-semibold transition-all ${filters.prepTime === 30 || filters.prepTime === '30' ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              🕐 30min
            </button>
            <button
              type="button"
              onClick={() => handleChange('prepTime')(filters.prepTime === 60 || filters.prepTime === '60' ? '' : 60)}
              className={`px-2 py-2 rounded-lg text-sm font-semibold transition-all ${filters.prepTime === 60 || filters.prepTime === '60' ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              🍳 60min+
            </button>
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Vegan</label>
          <div className="inline-flex rounded-full bg-gray-100 p-1 h-10">
            <button
              type="button"
              onClick={() => handleChange('vegan')('all')}
              className={`px-3 py-1 rounded-full text-sm font-semibold ${filters.vegan === 'all' ? 'bg-white text-gray-900 shadow' : 'text-gray-600 hover:bg-white'}`}
            >
              All
            </button>
            <button
              type="button"
              onClick={() => handleChange('vegan')('yes')}
              className={`px-3 py-1 rounded-full text-sm font-semibold ${filters.vegan === 'yes' ? 'bg-pink-600 text-white shadow' : 'text-gray-600 hover:bg-white'}`}
            >
              Vegan
            </button>
            <button
              type="button"
              onClick={() => handleChange('vegan')('no')}
              className={`px-3 py-1 rounded-full text-sm font-semibold ${filters.vegan === 'no' ? 'bg-white text-gray-900 shadow' : 'text-gray-600 hover:bg-white'}`}
            >
              Non-Vegan
            </button>
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">High-Protein</label>
          <div className="flex items-center gap-3">
            <div
              role="switch"
              aria-checked={filters.highProtein ? 'true' : 'false'}
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { const isOn = !!(filters.highProtein === true || filters.highProtein === 'true'); handleChange('highProtein')(!isOn); } }}
              onClick={() => { const isOn = !!(filters.highProtein === true || filters.highProtein === 'true'); handleChange('highProtein')(!isOn); }}
              className={`w-14 h-8 flex items-center p-1 rounded-full cursor-pointer transition-colors ${filters.highProtein === true || filters.highProtein === 'true' ? 'bg-pink-600' : 'bg-gray-200'}`}
            >
              <div className={`w-6 h-6 bg-white rounded-full shadow transform transition-transform ${filters.highProtein === true || filters.highProtein === 'true' ? 'translate-x-6' : 'translate-x-0'}`} />
            </div>
            <div className="text-sm text-gray-700">{filters.highProtein === true || filters.highProtein === 'true' ? 'Yes' : 'Any'}</div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Budget-Friendly</label>
          <div className="flex items-center gap-3">
            <div
              role="switch"
              aria-checked={filters.budgetFriendly ? 'true' : 'false'}
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { const isOn = !!(filters.budgetFriendly === true || filters.budgetFriendly === 'true'); handleChange('budgetFriendly')(!isOn); } }}
              onClick={() => { const isOn = !!(filters.budgetFriendly === true || filters.budgetFriendly === 'true'); handleChange('budgetFriendly')(!isOn); }}
              className={`w-14 h-8 flex items-center p-1 rounded-full cursor-pointer transition-colors ${filters.budgetFriendly === true || filters.budgetFriendly === 'true' ? 'bg-green-600' : 'bg-gray-200'}`}
            >
              <div className={`w-6 h-6 bg-white rounded-full shadow transform transition-transform ${filters.budgetFriendly === true || filters.budgetFriendly === 'true' ? 'translate-x-6' : 'translate-x-0'}`} />
            </div>
            <div className="text-sm text-gray-700">{filters.budgetFriendly === true || filters.budgetFriendly === 'true' ? 'Yes' : 'Any'}</div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Max Calories</label>
          <div className="flex items-center gap-4">
            <div className="relative w-full">
              {/* track */}
              <div className="h-3 rounded-full bg-gradient-to-r from-pink-500 to-purple-600 shadow-inner" />

              {/* filled portion */}
              <div
                className="absolute left-0 top-0 h-3 rounded-full bg-white/10"
                style={{ width: `${(Number(localMax) / 2000) * 100}%` }}
              />

              {/* thumb (visual) */}
              <div
                className="absolute top-1/2 transform -translate-y-1/2 w-7 h-7 bg-white rounded-full shadow-lg border-4 border-white"
                style={{ left: `${(Number(localMax) / 2000) * 100}%`, transform: 'translate(-50%, -50%)' }}
              />

              {/* invisible native input for accessibility */}
              <input
                type="range"
                min={0}
                max={2000}
                step={10}
                value={localMax}
                onChange={handleSliderChange}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                aria-label="Max Calories"
              />
            </div>

            <div className="w-24 text-right text-sm text-gray-700"><span className="font-bold text-purple-600">{(localMax && String(localMax) !== '0') ? `${localMax} kcal` : 'Any'}</span></div>
          </div>
        </div>

      </div>

      <div className="mt-6 flex items-center justify-end gap-3">
        <button
          onClick={onClear}
          className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
        >
          Clear All
        </button>
        {showSearchButton && (
          <button
            onClick={() => onSearch && onSearch(filters)}
            className="px-6 py-2 bg-pink-600 text-white rounded-lg font-semibold hover:bg-pink-700 transition"
          >
            Search
          </button>
        )}
      </div>
    </div>
  );
}
